/**
 * Convert a UTC ISO string to "YYYY-MM-DDTHH:MM" wall-clock time in the given IANA timezone.
 * Used to pre-fill datetime-local inputs in the coach's configured timezone.
 */
export function toDatetimeLocal(iso: string, timeZone: string): string {
  const d = new Date(iso);
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(d);
  const get = (type: string) => parts.find(p => p.type === type)?.value ?? '00';
  // hour12:false can return '24' for midnight — normalise to '00'
  const hour = get('hour') === '24' ? '00' : get('hour');
  return `${get('year')}-${get('month')}-${get('day')}T${hour}:${get('minute')}`;
}

/**
 * Convert "YYYY-MM-DDTHH:MM" wall-clock time in `timeZone` to a UTC ISO string.
 * Uses Intl to determine the timezone offset at that specific instant, correctly
 * handling DST transitions (spring-forward and fall-back).
 */
export function coachLocalToUtcIso(localDatetime: string, timeZone: string): string {
  const [datePart, timePart] = localDatetime.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hour, minute] = timePart.split(':').map(Number);

  const getOffsetMs = (utcMs: number): number => {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(new Date(utcMs));
    const get = (type: string) => Number(parts.find(p => p.type === type)?.value ?? '0');
    const h = get('hour') === 24 ? 0 : get('hour');
    const renderedMs = Date.UTC(get('year'), get('month') - 1, get('day'), h, get('minute'));
    return utcMs - renderedMs;
  };

  // Treat the wall-clock time as UTC to get a rough epoch, then get the offset there.
  const localMs = Date.UTC(year, month - 1, day, hour, minute);
  const firstPass = localMs + getOffsetMs(localMs);
  // The first-pass result may land on the wrong side of a DST boundary (e.g. spring-forward).
  // A second pass using the offset at the first-pass result corrects this in one step.
  return new Date(localMs + getOffsetMs(firstPass)).toISOString();
}
